/**
 * Shared filters for Matchups queries and client-side game lists.
 */

/** Elo-counting game types: mixed, mens/womens doubles, mens/womens singles. */
export const BALANCED_GAME_TYPE_IDS = [1, 2, 3, 4, 5];

export const ALL_MATCHUP_GAME_TYPE_IDS = [1, 2, 3, 4, 5, 6, 7, 8, 9];

export const DEFAULT_MATCHUP_GAME_TYPE_IDS = [...BALANCED_GAME_TYPE_IDS];

export const MATCHUP_GAME_TYPE_BUCKETS = [
  { value: 'mixed', label: 'Mixed' },
  { value: 'levels', label: 'Levels' },
  { value: 'singles', label: 'Singles' },
  { value: 'unbalanced', label: 'Unbalanced' },
];

export const DEFAULT_MATCHUP_GAME_TYPE_BUCKETS = ['mixed', 'levels', 'singles'];
export const ALL_MATCHUP_GAME_TYPE_BUCKETS = ['mixed', 'levels', 'singles', 'unbalanced'];

export const MATCHUP_DATE_PRESETS = [
  { value: 'all', label: 'All time' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 3 months' },
  { value: 'year', label: 'This year' },
  { value: 'custom', label: 'Custom' },
];

const PAIR_TYPE_TO_GAME_TYPE_IDS = {
  mixed: [1],
  levels: [2, 3],
  singles: [4, 5, 7],
};

export function isBalancedGameType(gameTypeId) {
  return BALANCED_GAME_TYPE_IDS.includes(Number(gameTypeId));
}

export function normalizeMatchupGender(gender) {
  const value = String(gender || '').trim().toLowerCase();
  if (value === 'male' || value === 'm') return 'male';
  if (value === 'female' || value === 'f') return 'female';
  return null;
}

export function normalizeGameTypeBuckets(buckets) {
  if (!Array.isArray(buckets)) return [];
  const allowed = new Set(ALL_MATCHUP_GAME_TYPE_BUCKETS);
  return [...new Set(buckets.filter((bucket) => allowed.has(bucket)))];
}

export function sameBucketSet(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((value) => set.has(value));
}

export function isDefaultBalancedBuckets(buckets) {
  return sameBucketSet(normalizeGameTypeBuckets(buckets), DEFAULT_MATCHUP_GAME_TYPE_BUCKETS);
}

export function isAllGameTypeBucketsSelected(buckets) {
  return sameBucketSet(normalizeGameTypeBuckets(buckets), ALL_MATCHUP_GAME_TYPE_BUCKETS);
}

/** Expand Mixed / Levels / Singles / Unbalanced into game_type_ids, scoped by gender. */
export function gameTypeIdsFromBuckets(buckets, gender) {
  const selected = new Set(normalizeGameTypeBuckets(buckets));
  const resolvedGender = normalizeMatchupGender(gender);
  const ids = [];

  if (selected.has('mixed')) ids.push(1);
  if (selected.has('levels')) {
    if (resolvedGender === 'male') ids.push(2);
    else if (resolvedGender === 'female') ids.push(3);
    else ids.push(2, 3);
  }
  if (selected.has('singles')) {
    if (resolvedGender === 'male') ids.push(4);
    else if (resolvedGender === 'female') ids.push(5);
    else ids.push(4, 5);
  }
  if (selected.has('unbalanced')) ids.push(6, 7, 8, 9);

  return ids;
}

export function normalizeGameTypeIds(gameTypeIds) {
  if (!Array.isArray(gameTypeIds)) return [];
  return [...new Set(gameTypeIds.map(Number).filter((id) => ALL_MATCHUP_GAME_TYPE_IDS.includes(id)))];
}

export function sameIdSet(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  const set = new Set(a.map(Number));
  return b.every((id) => set.has(Number(id)));
}

export function isAllGameTypesSelected(gameTypeIds) {
  return sameIdSet(normalizeGameTypeIds(gameTypeIds), ALL_MATCHUP_GAME_TYPE_IDS);
}

export function isDefaultBalancedGameTypes(gameTypeIds) {
  return sameIdSet(normalizeGameTypeIds(gameTypeIds), DEFAULT_MATCHUP_GAME_TYPE_IDS);
}

/**
 * Resolve selected game type IDs.
 * Prefers `gameTypeIds`. Falls back to the legacy pair-type + balancedOnly pair.
 */
export function resolveMatchupGameTypeIds({
  gameTypeIds,
  gameTypeBuckets,
  gender,
  gameType = 'all',
  balancedOnly = false,
} = {}) {
  if (Array.isArray(gameTypeBuckets)) {
    return gameTypeIdsFromBuckets(gameTypeBuckets, gender);
  }
  if (Array.isArray(gameTypeIds)) {
    return normalizeGameTypeIds(gameTypeIds);
  }

  let ids = balancedOnly ? [...BALANCED_GAME_TYPE_IDS] : [...ALL_MATCHUP_GAME_TYPE_IDS];
  if (gameType && gameType !== 'all') {
    const wanted = new Set(PAIR_TYPE_TO_GAME_TYPE_IDS[gameType] || []);
    ids = ids.filter((id) => wanted.has(id));
  }
  return ids;
}

export function applyMatchupGameTypeFilter(query, gameTypeIds) {
  const ids = normalizeGameTypeIds(gameTypeIds);
  if (ids.length === 0) {
    return query.eq('session_games.game_type_id', -1);
  }
  if (isAllGameTypesSelected(ids)) return query;
  return query.in('session_games.game_type_id', ids);
}

/** Restrict a session_games join query to Elo-counting types when the filter is on. */
export function applyMatchupBalancedFilter(query, balancedOnly) {
  return applyMatchupGameTypeFilter(
    query,
    balancedOnly ? BALANCED_GAME_TYPE_IDS : ALL_MATCHUP_GAME_TYPE_IDS
  );
}

export function filterGameGroupsByGameTypeIds(gameGroups, gameTypeIds) {
  const ids = new Set(normalizeGameTypeIds(gameTypeIds));
  if (ids.size === 0) return [];
  if (isAllGameTypesSelected([...ids])) return gameGroups;
  return gameGroups.filter((games) =>
    ids.has(Number(games[0]?.session_games?.game_type_id ?? games[0]?.game_type_id))
  );
}

export function filterGameGroupsByBalanced(gameGroups, balancedOnly) {
  return filterGameGroupsByGameTypeIds(
    gameGroups,
    balancedOnly ? BALANCED_GAME_TYPE_IDS : ALL_MATCHUP_GAME_TYPE_IDS
  );
}

function gameTypeIdFromMappedGame(game) {
  return game?.gameTypeId ?? game?.game_type_id ?? game?.session_games?.game_type_id;
}

export function resolveSeasonId(selectedSeason) {
  if (!selectedSeason) return null;
  return selectedSeason?.id ?? selectedSeason ?? null;
}

export function toMatchupDateInputValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function resolveMatchupDateRange(preset, dateFrom = '', dateTo = '', now = new Date()) {
  if (!preset || preset === 'all') {
    return { dateFrom: '', dateTo: '' };
  }
  if (preset === 'custom') {
    return { dateFrom: dateFrom || '', dateTo: dateTo || '' };
  }

  const end = toMatchupDateInputValue(now);
  if (preset === '30d') {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29);
    return { dateFrom: toMatchupDateInputValue(start), dateTo: end };
  }
  if (preset === '90d') {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 89);
    return { dateFrom: toMatchupDateInputValue(start), dateTo: end };
  }
  if (preset === 'year') {
    return { dateFrom: `${now.getFullYear()}-01-01`, dateTo: end };
  }
  return { dateFrom: '', dateTo: '' };
}

/** Apply date range to a Supabase query with session_games.created_at */
export function applyMatchupDateFilters(query, dateFrom, dateTo) {
  let q = query;
  if (dateFrom) {
    const start = dateFrom.length <= 10 ? `${dateFrom}T00:00:00.000Z` : dateFrom;
    q = q.gte('session_games.created_at', start);
  }
  if (dateTo) {
    const end = dateTo.length <= 10 ? `${dateTo}T23:59:59.999Z` : dateTo;
    q = q.lte('session_games.created_at', end);
  }
  return q;
}

/** Filter raw players_session_games groups (array of rows per game) */
export function filterGameGroupsByPairType(gameGroups, gameType) {
  if (!gameType || gameType === 'all') return gameGroups;
  return gameGroups.filter((games) => games[0]?.pair_type === gameType);
}

function gameTimestamp(game) {
  const raw = game.date ?? game.created_at ?? game.session_games?.created_at;
  return raw ? new Date(raw).getTime() : null;
}

/** Filter mapped game objects returned from H2H service or teammate-only builder */
export function filterGamesByMatchupFilters(
  games,
  { gameType = 'all', dateFrom = null, dateTo = null, balancedOnly = false, gameTypeIds } = {}
) {
  if (!games?.length) return games ?? [];

  const typeIds = new Set(resolveMatchupGameTypeIds({ gameTypeIds, gameType, balancedOnly }));
  let list = games.filter((g) => typeIds.has(Number(gameTypeIdFromMappedGame(g))));

  if (dateFrom || dateTo) {
    const fromMs = dateFrom ? new Date(dateFrom.length <= 10 ? `${dateFrom}T00:00:00` : dateFrom).getTime() : null;
    const toMs = dateTo ? new Date(dateTo.length <= 10 ? `${dateTo}T23:59:59.999` : dateTo).getTime() : null;
    list = list.filter((g) => {
      const t = gameTimestamp(g);
      if (t == null) return true;
      if (fromMs != null && t < fromMs) return false;
      if (toMs != null && t > toMs) return false;
      return true;
    });
  }
  return list;
}

/** Recompute win/loss stats from a filtered game list (individual mode games) */
export function statsFromIndividualGames(games, currentPlayerId) {
  let player1Wins = 0;
  let player2Wins = 0;
  games.forEach((g) => {
    const isP1 = g.player1Id === currentPlayerId;
    const won =
      (isP1 && g.player1Result === 'win') || (!isP1 && g.player2Result === 'win');
    if (isP1) {
      if (won) player1Wins += 1;
      else player2Wins += 1;
    } else {
      if (won) player2Wins += 1;
      else player1Wins += 1;
    }
  });
  const totalGames = games.length;
  const player1WinRate = totalGames > 0 ? (player1Wins / totalGames) * 100 : 0;
  const player2WinRate = totalGames > 0 ? (player2Wins / totalGames) * 100 : 0;
  return { player1Wins, player2Wins, totalGames, player1WinRate, player2WinRate };
}
